import React, { useState, useEffect, useMemo } from "react";
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Orders from "components/v1/Orders";
import Login from "components/v1/Login";
import LoginV2 from "components/v2/Login";
import ProductsDisplay from "components/v1/Products";
import ReturnCart from "components/v1/ReturnCart";
import Success from "components/v1/Success";
import Tracking from "components/v1/Tracking";
import ErrorPage from "components/v1/Error";
import WebFont from "webfontloader";
import { createTheme, ThemeProvider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addMerchantConfig,
  addRmsConfig,
  getMerchantConfig,
} from "features/config/configSlice";
import { configAPI } from "utils/api";
import {
  hasValidEncipherencode,
  sanitizeEncipherencode,
} from "utils/encipherencode";
// import Home from "components/Home";
import Footer from "components/v1/common/Footer";
import V2Footer from "components/v2/Common/Footer";
import MaintenancePage from "components/v1/Error/Maintenance";
import Presigned from "components/v1/Presigned";
import { toast } from "react-toastify";
import GeneratePayment from "components/v1/GeneratePayment";
import PickupImages from "components/v1/PickupImages";
import OrdersV2 from "components/v2/Orders";
import ProductsV2 from "components/v2/Products";
import ReturnCartV2 from "components/v2/ReturnCart";
import SuccessV2 from "components/v2/Success";
import TrackingV2 from "components/v2/Tracking";
import PresignedV2 from "components/v2/Presigned";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { detectInitialLanguage } from "i18n/language";
import {
  hasValidTrackingReference,
  isV2Path,
  stripVersionPrefix,
} from "utils/routes";
import {
  getSearchWithoutAuthKey,
  getTrackingAuthKey,
  hasRememberedTrackingAuth,
  isTrackingPath,
  rememberTrackingAuth,
  rememberTrackingAuthFallback,
  restorePreviousAccessToken,
} from "utils/trackingAuth";
import { rememberWarrantyClaimParam } from "utils/warrantyClaim";
import { getReturnFlowDeepLink } from "utils/returnFlowDeepLink";
// import styled from "styled-components";

function PresignedRoute({ loadConfigurations, setLoginLoader }) {
  const location = useLocation();
  const [params] = useSearchParams();
  const returnFlowDeepLink = getReturnFlowDeepLink(params);

  if (returnFlowDeepLink) {
    const query = params.toString();
    return (
      <Navigate
        replace
        to={{
          pathname: "/v2/presignedurl",
          search: query ? `?${query}` : "",
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <Presigned
      loadConfigurations={loadConfigurations}
      setLoginLoader={setLoginLoader}
    />
  );
}

function ProtectedRoute({ children, loginPath }) {
  const location = useLocation();
  const [params] = useSearchParams();
  rememberWarrantyClaimParam(params);
  const trackingAuthKey = getTrackingAuthKey(location.pathname, params);

  if (trackingAuthKey) {
    const fallbackQuery = rememberTrackingAuthFallback(params);
    rememberTrackingAuth(trackingAuthKey);

    return (
      <Navigate
        replace
        to={{
          pathname: location.pathname,
          search: fallbackQuery ? `?${fallbackQuery}` : "",
          hash: location.hash,
        }}
      />
    );
  }

  if (
    !isTrackingPath(location.pathname) &&
    hasRememberedTrackingAuth()
  ) {
    restorePreviousAccessToken();
  }

  const accessToken =
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    params.get("access_token");

  if (accessToken) {
    sessionStorage.setItem("access_token", accessToken);
    return children;
  }

  const nextTarget = stripVersionPrefix(location.pathname)
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  const nextParams = new URLSearchParams(getSearchWithoutAuthKey(params));

  if (nextTarget) {
    nextParams.set("nextTarget", nextTarget);
  }

  const queryString = nextParams.toString();
  sessionStorage.setItem("initialQueryParams", queryString);

  return (
    <Navigate
      replace
      to={`${loginPath}${queryString ? `?${queryString}` : ""}`}
    />
  );
}

function PaymentCallbackRoute({
  children,
  loginPath = "/",
  allowTransactionOnly = false,
}) {
  const [params] = useSearchParams();
  const hasPaymentCallbackParams =
    hasValidTrackingReference(params.get("order_id")) &&
    (hasValidTrackingReference(params.get("reference_id")) ||
      (allowTransactionOnly &&
        hasValidTrackingReference(params.get("txn_id"))));

  if (hasPaymentCallbackParams) {
    return children;
  }

  return <ProtectedRoute loginPath={loginPath}>{children}</ProtectedRoute>;
}

function App() {
  const { t } = useTranslation();
  const [loginLoader, setLoginLoader] = useState(true);
  const [loadConfigurations, setLoadConfigurations] = useState(false);
  const [rmsError, setRMSError] = useState(null);
  const dispatch = useDispatch();

  const [params, setParams] = useSearchParams();

  useEffect(() => {
    rememberWarrantyClaimParam(params);
  }, [params]);

  const cname = window.location.hostname;

  const test_mode = params.get("test_mode");

  // const encipherencode =
  //   sessionStorage.getItem("encipherencode") || paramsEncipherenCode;

  const navigate = useNavigate();
  const location = useLocation();
  const unversionedPathname = stripVersionPrefix(location.pathname);

  // ── Shopify App Proxy: iframe detection ──
  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }, []);

  useEffect(() => {
    const skipRoutes = ["/", "/returns", "/presignedurl", "/error", "/maintenance", "/apps/pragma/returns"];
    if (isInIframe && !skipRoutes.includes(unversionedPathname)) {
      localStorage.setItem("rms_last_route", location.pathname);
      localStorage.setItem("rms_last_route_source", "iframe");
    }
  }, [isInIframe, location.pathname, unversionedPathname]);

  toast.configure();

  Sentry.init({
    dsn: "https://0322b1ac542e40c36e02007679ba0ea5@sentry.logisy.tech/12",
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    enabled:
      process.env.NODE_ENV === "production" &&
      window.location.hostname !== "localhost",
  });

  // const StyledBanner = styled.div`
  //   background: #f5c6ce;
  //   color: #dd435d;
  //   text-align: center;
  //   padding: 25px;
  //   font-size: 18px;
  //   line-height: 1.2;
  //   position: fixed;
  //   top: 0;
  //   width: 100%;
  //   z-index: 99;

  //   @media (max-width: 800px) {
  //     font-size: 14px;
  //   }
  // `;

  const merchantConfig = useSelector(getMerchantConfig);
  const fontFamily = merchantConfig?.google_font_family || "Inter";
  const fontStack = [fontFamily, "Inter", "sans-serif"].join(", ");

  // ── Shopify App Proxy: freeze all vh units to fixed px ──
  // Inside an iframe, "100vh" = iframe viewport height. When the parent resizes
  // the iframe based on content, 100vh changes → content changes → infinite loop.
  // Fix: convert every vh value to a fixed px value based on the INITIAL viewport.
  useEffect(() => {
    if (!isInIframe) return;

    const baseVh = window.innerHeight;
    const vhRegex = /(\d+(?:\.\d+)?)vh/g;
    const replaceVh = (text) =>
      text.replace(vhRegex, (_, n) => `${(parseFloat(n) * baseVh) / 100}px`);

    // Prong 1: Rewrite existing CSSOM rules (styled-components already injected)
    for (const sheet of document.styleSheets) {
      try {
        const rules = sheet.cssRules;
        if (!rules) continue;
        for (let i = 0; i < rules.length; i++) {
          const txt = rules[i].cssText;
          if (txt && txt.includes("vh")) {
            sheet.deleteRule(i);
            sheet.insertRule(replaceVh(txt), i);
          }
        }
      } catch (_) {
        // cross-origin sheet — skip
      }
    }

    // Prong 2: Monkey-patch insertRule for future styled-components (production mode)
    const origInsertRule = CSSStyleSheet.prototype.insertRule;
    CSSStyleSheet.prototype.insertRule = function (rule, index) {
      return origInsertRule.call(
        this,
        rule.includes("vh") ? replaceVh(rule) : rule,
        index
      );
    };

    // Prong 3: Rewrite inline style attributes containing vh
    const fixInline = (el) => {
      const raw = el.getAttribute("style");
      if (raw && raw.includes("vh")) {
        el.setAttribute("style", replaceVh(raw));
      }
    };
    document.querySelectorAll("[style]").forEach(fixInline);

    // Rewrite any existing <style> tag textContent (dev mode styled-components)
    document.querySelectorAll("style").forEach((el) => {
      if (el.textContent && el.textContent.includes("vh")) {
        el.textContent = replaceVh(el.textContent);
      }
    });

    // MutationObserver: catch dynamically added nodes & style changes
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.tagName === "STYLE" && node.textContent?.includes("vh")) {
              node.textContent = replaceVh(node.textContent);
            }
            fixInline(node);
            node.querySelectorAll?.("[style]").forEach(fixInline);
          }
        }
        if (m.type === "attributes" && m.attributeName === "style") {
          fixInline(m.target);
        }
      }
    });
    mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    // Ensure html/body/root don't constrain content height
    const resetStyle = document.createElement("style");
    resetStyle.textContent =
      "html, body, #root { height: auto !important; min-height: 0 !important; overflow: visible !important; }";
    document.head.appendChild(resetStyle);

    return () => {
      mo.disconnect();
      CSSStyleSheet.prototype.insertRule = origInsertRule;
      resetStyle.remove();
    };
  }, [isInIframe]);

  // ── Shopify App Proxy: auto-resize — post height to parent ──
  useEffect(() => {
    if (!isInIframe) return;
    let lastHeight = 0;
    let timer = null;
    const rootEl = document.getElementById("root");
    const targetOrigin = (() => {
      try {
        return new URL(document.referrer).origin || "*";
      } catch (e) {
        return "*";
      }
    })();
    if (!rootEl) return;

    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const height = rootEl.scrollHeight;
        if (Math.abs(height - lastHeight) > 5) {
          lastHeight = height;
          window.parent.postMessage(
            { type: "pragma-returns-resize", height },
            targetOrigin
          );
        }
      }, 200);
    });
    ro.observe(rootEl);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [isInIframe]);

  useEffect(() => {
    const syncLanguage = () => {
      const nextLanguage = detectInitialLanguage();

      i18n.changeLanguage(nextLanguage);
      document.documentElement.lang = nextLanguage;
    };

    syncLanguage();
    window.addEventListener("languagechange", syncLanguage);

    return () => {
      window.removeEventListener("languagechange", syncLanguage);
    };
  }, []);

  useEffect(() => {
    const isLocalhost = cname === "localhost";
    const defaultCode =
      "gAAAAABmhl81_N038e-lfPa1zKoaIhpAyHCuCb17QzbP4W5NBHy9noZnuBcs8QxE0L28hiFclPIkTjMiZIQ3T7yAwSOE0-iwtA==";
    const paramsCode = sanitizeEncipherencode(params.get("encipherencode"));
    const storedCode = sanitizeEncipherencode(
      sessionStorage.getItem("encipherencode")
    );
    // prefer session, else URL param, else default on localhost
    const encipherencode =
      paramsCode ?? storedCode ?? (isLocalhost ? defaultCode : null);

    // const currentCode =
    //   paramsEncipherenCode ?? (isLocalhost ? defaultCode : encipherencode);

    const fetchConfig = async (code, name) => {
      if (!code) {
        navigate("/error");
        return;
      }
      try {
        const resp = await configAPI(code, name, test_mode);

        if (resp.data.under_maintenance) {
          navigate("/maintenance");
          return;
        }

        dispatch(addRmsConfig(resp.data.rms_config));
        dispatch(addMerchantConfig(resp.data.merchant_config));
        setLoadConfigurations(true);
      } catch (err) {
        navigate("/error");
        setRMSError(err);
      } finally {
        try {
          const validCode = sanitizeEncipherencode(
            isLocalhost ? defaultCode : encipherencode
          );
          if (validCode) {
            sessionStorage.setItem("encipherencode", validCode);
          }
        } catch {}
      }
    };

    if (!hasValidEncipherencode(encipherencode)) {
      navigate("/error");
      return;
    }

    // Persisted configuration is only a cache. Refresh it on every portal entry
    // so changes to the customer journey and return rules take effect on reload.
    fetchConfig(encipherencode, isLocalhost ? null : cname);

    if (window.location.search) {
      try {
        const initialParams = new URLSearchParams(window.location.search);
        initialParams.delete("auth_key");
        const path = stripVersionPrefix(window.location.pathname)
          .replace(/^\/+/, "")
          .replace(/\/+$/, "");
        if (path) {
          initialParams.set("nextTarget", path);
        }
        const withNextTarget = initialParams.toString();
        sessionStorage.setItem("initialQueryParams", withNextTarget);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const head = document.head || document.getElementsByTagName("head")[0];

    const setOrUpdateElement = (tag, attributes) => {
      const selector = attributes.name
        ? `${tag}[name='${attributes.name}']`
        : attributes.property
        ? `${tag}[property='${attributes.property}']`
        : attributes.rel
        ? `${tag}[rel='${attributes.rel}']`
        : tag;

      let element = document.querySelector(selector);

      if (!element) {
        element = document.createElement(tag);
        Object.keys(attributes).forEach((key) => {
          element[key] = attributes[key];
        });
        head.appendChild(element);
      } else {
        Object.keys(attributes).forEach((key) => {
          element[key] = attributes[key];
        });
      }
    };

    const scripts = [
      {
        src: merchantConfig?.ga_head_script,
        async: true,
      },
      {
        innerHTML: merchantConfig?.ga_body_script,
        type: "text/javascript",
      },
    ];

    scripts.forEach((scriptAttrs) => {
      if (scriptAttrs.src || scriptAttrs.innerHTML) {
        const script = document.createElement("script");
        Object.keys(scriptAttrs).forEach((key) => {
          script[key] = scriptAttrs[key];
        });
        head.appendChild(script);
      }
    });

    if (fontFamily) {
      try {
        const googleFamilies =
          fontFamily.toLowerCase() === "inter" ||
          fontFamily.toLowerCase().includes("soleil")
            ? ["Inter"]
            : [fontFamily, "Inter"];

        if (fontFamily.toLowerCase().includes("soleil")) {
          WebFont.load({
            custom: {
              families: ["Soleil"],
              urls: ["/fonts/soleil.css"],
            },
            google: {
              families: googleFamilies,
            },
          });
        } else {
          WebFont.load({
            google: {
              families: googleFamilies,
            },
          });
        }
      } catch (err) {}
      document.body.style.fontFamily = fontStack;
    } else {
      WebFont.load({
        google: {
          families: ["Inter"],
        },
      });
      document.body.style.fontFamily = "Inter, sans-serif";
    }

    if (merchantConfig && Object.keys(merchantConfig).length > 0) {
      const faviconHref =
        merchantConfig.favicon || `${process.env.PUBLIC_URL}/favicon.svg`;

      setOrUpdateElement("link", { rel: "icon", href: faviconHref });
      setOrUpdateElement("link", { rel: "shortcut icon", href: faviconHref });
      setOrUpdateElement("title", {
        innerText: t("common.portalTitle", {
          shopName: merchantConfig.shop_name,
        }),
      });
      setOrUpdateElement("meta", {
        name: "description",
        content: t("common.portalDescription"),
      });
      setOrUpdateElement("meta", {
        property: "og:title",
        content: t("common.portalTitle", {
          shopName: merchantConfig.shop_name,
        }),
      });
      setOrUpdateElement("meta", {
        property: "og:description",
        content: t("common.portalDescription"),
      });
      setOrUpdateElement("meta", {
        property: "og:image",
        content: faviconHref,
      });
      setOrUpdateElement("meta", {
        property: "og:image:type",
        content: "image/svg+xml",
      });
      setOrUpdateElement("meta", {
        name: "twitter:card",
        content: "summary",
      });
      setOrUpdateElement("meta", {
        name: "twitter:title",
        content: t("common.portalTitle", {
          shopName: merchantConfig.shop_name,
        }),
      });
      setOrUpdateElement("meta", {
        name: "twitter:description",
        content: t("common.portalDescription"),
      });
      setOrUpdateElement("meta", {
        name: "twitter:image",
        content: faviconHref,
      });
    }
  }, [merchantConfig, fontFamily, fontStack, t]);

  const theme = createTheme({
    typography: {
      fontFamily: fontStack,
    },
    palette: {
      primary: {
        main:
          merchantConfig?.primary_theme_color !== undefined
            ? merchantConfig.primary_theme_color
            : "#111",
        contrastText:
          merchantConfig?.secondary_theme_color !== undefined
            ? merchantConfig.secondary_theme_color
            : "#fff",
      },
    },
  });

  // Authenticated deep links also need current rules before their screens mount.
  // Error and maintenance pages must remain reachable while config is unavailable.
  if (
    !loadConfigurations &&
    !["/error", "/maintenance"].includes(unversionedPathname)
  ) {
    return (
      <div className="App" role="status" aria-live="polite">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        {/* <StyledBanner>
          Returns system is facing some issues. Please try again in a few hours.
        </StyledBanner> */}
        {/* <iframe
          id="sessionIframe"
          style={{ display: "none" }}
          src="https://orders.logisy.tech/session"
        ></iframe> */}
        <Routes>
          <Route
            exact
            path="/"
            element={
              <Login
                loginLoader={loginLoader}
                loadConfigurations={loadConfigurations}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            exact
            path="/v2"
            element={
              <LoginV2
                loginLoader={loginLoader}
                loadConfigurations={loadConfigurations}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="apps/pragma/returns"
            element={
              <Login
                loadConfigurations={loadConfigurations}
                loginLoader={loginLoader}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="/returns"
            element={
              <Login
                loadConfigurations={loadConfigurations}
                loginLoader={loginLoader}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="/v2/returns"
            element={
              <LoginV2
                loadConfigurations={loadConfigurations}
                loginLoader={loginLoader}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="/v2/apps/pragma/returns"
            element={
              <LoginV2
                loadConfigurations={loadConfigurations}
                loginLoader={loginLoader}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="/presignedurl"
            element={
              <PresignedRoute
                loadConfigurations={loadConfigurations}
                setLoginLoader={setLoginLoader}
              />
            }
          />
          <Route
            path="/v2/presignedurl"
            element={<PresignedV2 loadConfigurations={loadConfigurations} />}
          />
          <Route
            exact
            path="/maintenance"
            element={
              <MaintenancePage
                setLoginLoader={setLoginLoader}
                setLoadConfigurations={setLoadConfigurations}
              />
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute loginPath="/">
                <Orders setLoginLoader={setLoginLoader} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/orders"
            element={
              <ProtectedRoute loginPath="/v2">
                <OrdersV2 setLoginLoader={setLoginLoader} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute loginPath="/">
                <ProductsDisplay />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/products"
            element={
              <ProtectedRoute loginPath="/v2">
                <ProductsV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/return-cart"
            element={
              <ProtectedRoute loginPath="/">
                <ReturnCart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/return-cart"
            element={
              <PaymentCallbackRoute
                loginPath="/v2"
                allowTransactionOnly
              >
                <ReturnCartV2 />
              </PaymentCallbackRoute>
            }
          />
          <Route
            path="/generate-payment"
            element={
              <PaymentCallbackRoute>
                <GeneratePayment />
              </PaymentCallbackRoute>
            }
          />
          <Route
            path="/success"
            element={
              <ProtectedRoute loginPath="/">
                <Success />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/success"
            element={
              <ProtectedRoute loginPath="/v2">
                <SuccessV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking"
            element={
              <ProtectedRoute loginPath="/">
                <Tracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/tracking"
            element={
              <ProtectedRoute loginPath="/v2">
                <TrackingV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/pickup-image-submission"
            element={
              <Navigate
                replace
                to={{ pathname: "/v2/tracking", search: location.search }}
              />
            }
          />
          <Route
            path="/pickup-image-submission"
            element={<PickupImages />}
          />
          <Route path="/error" element={<ErrorPage rmsError={rmsError} />} />
        </Routes>
        {merchantConfig &&
          merchantConfig.is_pragma_branding_visible &&
          unversionedPathname !== "/" &&
          unversionedPathname !== "/returns" &&
          (isV2Path(location.pathname) ? (
            <V2Footer stickyBottom />
          ) : (
            <Footer />
          ))}
      </ThemeProvider>
    </div>
  );
}

export default App;
